import {useCallback, useRef, useState} from 'react';

import {assert} from 'Asserts';
import runModal from 'ui-library/runModal';

import FlowchartConfirmExitWithUnsavedChangesModal, {
  FlowchartConfirmExitWithUnsavedChangesModalProps,
} from '../modals/FlowchartConfirmExitWithUnsavedChangesModal';
import {FlowchartNode} from '../processFlowchartConfigEditorNodeTypes';

/**
 * Registered state for a sidepanel tab to track unsaved changes.
 */
export interface SidepanelTabState {
  // This needs to be unique for each tab, recommend a descriptive name or UUID.
  tabId: string;
  isDirty: boolean;
  onDiscard?: () => Promise<void> | void;
}

export type SidepanelRegisterTabState = Omit<SidepanelTabState, 'isDirty'>;

/**
 * Internal state of the sidepanel manager, determines which sidepanel is open, and the node and field that are selected.
 */
export interface SidepanelInternalState {
  node: FlowchartNode | null;
  fieldId: string | null;
  openSidepanelType: SidepanelType | null;
}

const EMPTY_SIDEPANEL_INTERNAL_STATE: SidepanelInternalState = {
  node: null,
  fieldId: null,
  openSidepanelType: null,
};

/**
 * Which sidepanel is currently open or being requested to open.
 */
export enum SidepanelType {
  /** Sidepanel responsible for node-level and field-level configuration
   *
   * In Benchling flowcharts, this should be on the right side of the screen.
   */
  NODE_SETTINGS = 'NODE_SETTINGS',
  /** Sidepanel responsible for flowchart-level configuration
   *
   * In Benchling flowcharts, this should be on the left side of the screen.
   */
  FLOWCHART_SETTINGS = 'FLOWCHART_SETTINGS',
}

/**
 * Request to change the sidepanel state by specifying the sidepanel and the node and field to select.
 */
export type SidepanelChange =
  | {
      sidepanel: SidepanelType;
      nodeId: string | null;
      fieldId?: string | null;
    }
  | {sidepanel: null; nodeId?: never; fieldId?: never};

interface UseFlowchartSidepanelManagerReturn {
  registerTab: (params: SidepanelRegisterTabState) => void;
  unregisterTab: (tab: string) => void;
  setIsTabDirty: (tab: string, isDirty: boolean) => void;
  changeTab: (newTab: string, activeTab: string) => Promise<boolean>;
  changeSidepanel: (sidepanelChange: SidepanelChange) => Promise<boolean>;
  getEffectiveState: () => SidepanelInternalState;
  forceClose: () => void;
}

/**
 * Manages all sidepanel states in the flowchart config editor.
 *
 * Tracks state for both the right and left sidepanels. As each sidepanel opens,
 * its tabs can register to track unsaved changes. Prevents navigating away from
 * tabs when there are unsaved changes and uses a request-style pattern for
 * sidepanel changes. Ensures only one sidepanel can be open at a time.
 *
 * @param getNodeById - Function to get an interactivenode by its ID.
 * @returns Object with methods to put into the sidepanel manager context.
 */
export default function useFlowchartSidepanelManager(
  getNodeById: (nodeId: string) => FlowchartNode | null
): UseFlowchartSidepanelManagerReturn {
  // Using refs to ensure changes to dirty state don't need a full render cycle before we can check against it
  // when changing the tabs or sidepanel state.
  const tabStates = useRef<Map<string, SidepanelTabState>>(new Map());
  const isModalOpenRef = useRef(false);

  const [sidepanelState, setSidepanelState] = useState<SidepanelInternalState>({
    ...EMPTY_SIDEPANEL_INTERNAL_STATE,
  });

  const getHasDirtyTabs = useCallback(
    () => Array.from(tabStates.current.values()).some((state) => state.isDirty),
    []
  );

  const clearAllTabStates = useCallback(() => {
    tabStates.current = new Map();
  }, []);

  const registerTab = useCallback((params: SidepanelRegisterTabState) => {
    const {tabId, onDiscard} = params;
    const existingState = tabStates.current.get(tabId);

    if (existingState) {
      tabStates.current.set(tabId, {...existingState, onDiscard});
    } else {
      tabStates.current.set(tabId, {tabId, isDirty: false, onDiscard});
    }
  }, []);

  const unregisterTab = useCallback((tabId: string) => {
    tabStates.current.delete(tabId);
  }, []);

  const setIsTabDirty = useCallback((tabId: string, dirty: boolean) => {
    const currentState = tabStates.current.get(tabId);
    if (currentState && currentState.isDirty !== dirty) {
      tabStates.current.set(tabId, {...currentState, isDirty: dirty});
    }
  }, []);

  const discardAllChanges = useCallback(() => {
    tabStates.current.forEach((tabState) => {
      if (tabState.onDiscard) {
        tabState.onDiscard();
      }
    });

    clearAllTabStates();
  }, [clearAllTabStates]);

  const markModalClosed = useCallback(() => {
    isModalOpenRef.current = false;
  }, []);

  const applyIncomingState = useCallback(
    (incomingState: SidepanelInternalState) => {
      setSidepanelState(incomingState);
      markModalClosed();
    },
    [markModalClosed]
  );

  const handleModalKeepEditing = useCallback(
    (resolve: (value: boolean) => void) => {
      markModalClosed();
      resolve(false);
    },
    [markModalClosed]
  );

  const handleModalLeaveWithoutSaving = useCallback(
    (incomingState: SidepanelInternalState, resolve: (value: boolean) => void) => {
      discardAllChanges();
      applyIncomingState(incomingState);
      resolve(true);
    },
    [discardAllChanges, applyIncomingState]
  );

  const showUnsavedChangesModal = useCallback(
    (incomingState: SidepanelInternalState): Promise<boolean> => {
      if (isModalOpenRef.current) {
        return Promise.resolve(false);
      }

      assert(sidepanelState.node != null, 'Sidepanel state must have a node');
      const nodeName = sidepanelState.node.data.nodeName;

      return new Promise<boolean>((resolve) => {
        isModalOpenRef.current = true;

        runModal<FlowchartConfirmExitWithUnsavedChangesModalProps>(
          FlowchartConfirmExitWithUnsavedChangesModal,
          {
            nodeName,
            // This is a quirk of runModal, keep editing will be closed on both the button and X clicks
            // But this field and wrapped later on to handle the actual close logic.
            onRequestClose: () => {},
            onRequestKeepEditing: () => {
              handleModalKeepEditing(resolve);
            },
            onRequestLeaveWithoutSaving: () => {
              handleModalLeaveWithoutSaving(incomingState, resolve);
            },
          }
        );
      });
    },
    [handleModalKeepEditing, handleModalLeaveWithoutSaving, sidepanelState]
  );

  /**
   * Attempts to change the tab to the requested state, within the sidepanel.
   *
   * This should be called in any place where a tab change would be triggered.
   *
   * @param newTabId - The ID of the new tab.
   * @param activeTabId - The ID of the active tab.
   * @returns True if the tab can be changed or is already the same state, false otherwise. If false, caller
   * is responsible for ensuring the requested change is not applied.
   */
  const changeTab = useCallback(
    async (newTabId: string, activeTabId: string): Promise<boolean> => {
      if (newTabId === activeTabId) {
        return true;
      }

      const currentState = tabStates.current.get(activeTabId);
      if (currentState?.isDirty) {
        return await showUnsavedChangesModal(sidepanelState);
      }

      return true;
    },
    [showUnsavedChangesModal, sidepanelState]
  );

  const forceClose = useCallback(() => {
    // There are situations that supersede discarding unsaved changes
    // (Ex: The selected node or field is being deleted)
    // Just reset the sidepanel state to the empty state
    clearAllTabStates();
    setSidepanelState(EMPTY_SIDEPANEL_INTERNAL_STATE);
  }, [clearAllTabStates]);

  const buildStateToApply = useCallback(
    (sidepanelChange: SidepanelChange): SidepanelInternalState => {
      const node = sidepanelChange.nodeId != null ? getNodeById(sidepanelChange.nodeId) : null;

      return {
        node,
        fieldId: sidepanelChange.fieldId ?? null,
        openSidepanelType: sidepanelChange.sidepanel,
      };
    },
    [getNodeById]
  );

  const areSidepanelStatesEqual = useCallback(
    (state1: SidepanelInternalState, state2: SidepanelInternalState): boolean => {
      const isSameNodeId = state1.node?.id === state2.node?.id;
      const isSameFieldId = state1.fieldId === state2.fieldId;
      const isSameOpenSidepanelType = state1.openSidepanelType === state2.openSidepanelType;

      return isSameNodeId && isSameFieldId && isSameOpenSidepanelType;
    },
    []
  );

  const shouldUpdateNodeData = useCallback(
    (stateToApply: SidepanelInternalState): boolean => {
      const isSameNodeId = stateToApply.node?.id === sidepanelState.node?.id;
      const isNodeDataChanged = stateToApply.node !== sidepanelState.node;

      return isSameNodeId && isNodeDataChanged && !getHasDirtyTabs();
    },
    [sidepanelState, getHasDirtyTabs]
  );

  const applySidepanelChange = useCallback(
    async (stateToApply: SidepanelInternalState): Promise<boolean> => {
      const stayingOnSameNode = stateToApply.node?.id === sidepanelState.node?.id;
      const stayingOnSameSidepanel = stateToApply.openSidepanelType === sidepanelState.openSidepanelType;
      const hasDirtyTabs = getHasDirtyTabs();

      if (hasDirtyTabs && (!stayingOnSameNode || !stayingOnSameSidepanel)) {
        return await showUnsavedChangesModal(stateToApply);
      }
      setSidepanelState(stateToApply);
      return true;
    },
    [getHasDirtyTabs, showUnsavedChangesModal, sidepanelState]
  );

  /**
   * Attempts to change the sidepanel to the requested state.
   *
   * This should be called in any place where a sidepanel change would be triggered.
   *
   * @param sidepanelChange - The sidepanel change request.
   * @returns True if the sidepanel was changed or is already the same state, false otherwise. If false,
   * caller is responsible for ensuring the requested change is not applied.
   */
  const changeSidepanel = useCallback(
    async (sidepanelChange: SidepanelChange): Promise<boolean> => {
      const stateToApply = buildStateToApply(sidepanelChange);

      if (shouldUpdateNodeData(stateToApply)) {
        setSidepanelState(stateToApply);
        return true;
      }

      if (areSidepanelStatesEqual(stateToApply, sidepanelState)) {
        return true;
      }

      return await applySidepanelChange(stateToApply);
    },
    [sidepanelState, buildStateToApply, shouldUpdateNodeData, areSidepanelStatesEqual, applySidepanelChange]
  );

  const getEffectiveState = useCallback((): SidepanelInternalState => {
    // Always return the latest node data by looking it up via getNodeById
    // This ensures we get updated node data without needing state updates
    // This may eventually become a performance bottleneck if we are refreshing the node data frequently.
    const latestNode = sidepanelState.node?.id != null ? getNodeById(sidepanelState.node.id) : null;
    return {
      ...sidepanelState,
      node: latestNode,
    };
  }, [sidepanelState, getNodeById]);

  return {
    registerTab,
    unregisterTab,
    setIsTabDirty,
    changeTab,
    forceClose,
    getEffectiveState,
    changeSidepanel,
  };
}
